import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDesa,
  useEtag,
  usePlantilles,
  usePrevisualitzaPlantilla,
  type PlantillaDto,
  type TraduccioPlantillaDto,
} from '@/api/administracio';
import type { Canal } from '@/api/types';
import { Desplegable } from '@/components/Desplegable';
import { Icona } from '@/components/Icona';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { Buit, Camp } from '@/pages/operacio/comu';
import { useMe } from '@/permisos/MeContext';
import { Permis } from '@/permisos/Permis';
import { CapPanell, Cerca, ErrorDesa } from './comu';
import { BarraCanvis } from './marc/canvis';
import { useGuardaCanvis } from './marc/canvisContext';
import { substitueix } from './utils';
import './administracio.css';
import './avisos.css';

const IDIOMES = ['ca', 'es'] as const;
type Idioma = (typeof IDIOMES)[number];
const CANALS_PREVIA: Canal[] = ['PUSH', 'EMAIL', 'TRUCADA'];
const CAMPS_TEXT = ['titol', 'cos', 'cosPush', 'cosVeu'] as const;
type CampText = (typeof CAMPS_TEXT)[number];
const [C_TITOL, C_COS, C_PUSH, C_VEU] = CAMPS_TEXT;
/** Valor del desplegable de tipus que obre un camp de text lliure. */
const ALTRE = '__altre__';

const buida = (): TraduccioPlantillaDto => ({ titol: '', cos: '' });
const novaPlantilla = (): PlantillaDto => ({
  codi: '',
  tipus: 'AVIS',
  variables: ['zona', 'servei', 'inici'],
  actiu: true,
  traduccions: { ca: buida(), es: buida() },
});
const completa = (tr?: TraduccioPlantillaDto) => !!tr?.titol.trim() && !!tr?.cos.trim();
const titolDe = (p: PlantillaDto) => p.traduccions.ca?.titol || p.traduccions.es?.titol || p.codi;

/** Nombre de camps modificats (generals i de cada idioma). */
function compteCanvis(a: PlantillaDto, b: PlantillaDto): number {
  if (a.id == null) return 1;
  let n = 0;
  if (a.codi !== b.codi) n++;
  if (a.tipus !== b.tipus) n++;
  if (a.actiu !== b.actiu) n++;
  for (const i of IDIOMES)
    for (const c of CAMPS_TEXT)
      if ((a.traduccions[i]?.[c] ?? '') !== (b.traduccions[i]?.[c] ?? '')) n++;
  return n;
}

function Editor({
  inicial,
  tipusExistents,
  onCreada,
}: {
  inicial: PlantillaDto;
  tipusExistents: string[];
  onCreada: (p: PlantillaDto) => void;
}) {
  const { t } = useTranslation('administracio');
  const tc = useTranslation('comu').t;
  const { pot } = useMe();
  const [base, setBase] = useState(inicial);
  const [p, setP] = useState(inicial);
  const { etag } = useEtag('/plantilles', p.id);
  const desa = useDesa<PlantillaDto>('/plantilles', ['plantilles'], etag);
  const previa = usePrevisualitzaPlantilla();
  const [idioma, setIdioma] = useState<Idioma>('ca');
  const [canal, setCanal] = useState<Canal>('PUSH');
  const [valors, setValors] = useState<Record<string, string>>({});
  const [tipusLliure, setTipusLliure] = useState(false);
  const textos = useRef<HTMLDivElement>(null);
  const [ultimCamp, setUltimCamp] = useState<CampText>('cos');
  const editable = pot('cai.plantilles', p.id == null ? 'ALTA' : 'MODIFICAR');
  const tr = p.traduccions[idioma] ?? buida();
  const canvis = compteCanvis(p, base);

  const canvia = (c: Partial<TraduccioPlantillaDto>) =>
    setP((ant) => ({
      ...ant,
      traduccions: {
        ...ant.traduccions,
        [idioma]: { ...(ant.traduccions[idioma] ?? buida()), ...c },
      },
    }));

  /** Insereix `{{variable}}` al cursor de l'últim camp de text on s'ha escrit. */
  const insereix = (v: string) => {
    const token = `{{${v}}}`;
    const el = textos.current?.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      `[data-camp="${ultimCamp}"]`,
    );
    const actual = (tr[ultimCamp] ?? '') as string;
    const ini = el?.selectionStart ?? actual.length;
    const fi = el?.selectionEnd ?? actual.length;
    canvia({ [ultimCamp]: `${actual.slice(0, ini)}${token}${actual.slice(fi)}` });
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(ini + token.length, ini + token.length);
    });
  };

  const textCamp = (c: CampText) => ({
    'data-camp': c,
    value: (tr[c] ?? '') as string,
    disabled: !editable,
    onFocus: () => setUltimCamp(c),
    onChange: (e: { target: { value: string } }) =>
      canvia({ [c]: c === 'cosPush' || c === 'cosVeu' ? e.target.value || null : e.target.value }),
  });

  const cosDe = (c: Canal) =>
    c === 'PUSH' ? tr.cosPush || tr.cos : c === 'TRUCADA' ? tr.cosVeu || tr.cos : tr.cos;
  const mostra = (c: Canal) =>
    previa.data && previa.data.canal === c && previa.data.idioma === idioma
      ? previa.data
      : { titol: substitueix(tr.titol, valors), cos: substitueix(cosDe(c), valors) };
  const desar = () =>
    desa.mutate(p, {
      onSuccess: (desada) => {
        const nova = { ...p, ...(desada ?? {}) };
        setBase(nova);
        setP(nova);
        // Una plantilla nova passa a ser la seleccionada: el segon «Desa» fa PUT, no un altre POST.
        if (p.id == null && desada?.id != null) onCreada(nova);
      },
    });

  const opcionsTipus = [
    ...[...new Set([...tipusExistents, p.tipus].filter(Boolean))].map((x) => ({
      valor: x,
      etiqueta: x,
    })),
    { valor: ALTRE, etiqueta: t('avisos.plantilles.altreTipus') },
  ];
  const vista = mostra(canal);

  return (
    <div className="lf__fitxa" data-plantilla={p.id ?? 'nova'}>
      <div className="fitxa-cap">
        <div className="fitxa-cap__titols">
          <span className="fitxa-cap__ruta">{t('titols.plantilles')}</span>
          <h2>{titolDe(p) || t('plantilles.nova')}</h2>
          <div className="fitxa-cap__badges">
            {p.tipus ? <span className="badge badge--preventiu">{p.tipus}</span> : null}
            {!p.actiu ? <span className="badge badge--gray">{t('comu.inactiu')}</span> : null}
          </div>
        </div>
      </div>
      <div className="fitxa-cos">
        <div className="panel">
          <div className="panel__body form">
            <div className="grid grid-3" style={{ gap: 12, alignItems: 'end' }}>
              <Camp etiqueta={t('plantilles.codi')}>
                <input
                  className="input mono"
                  value={p.codi}
                  disabled={!editable || p.id != null}
                  onChange={(e) => setP({ ...p, codi: e.target.value.toUpperCase() })}
                />
              </Camp>
              <div className="camp">
                <span className="camp__label">{t('plantilles.tipus')}</span>
                {tipusLliure ? (
                  <input
                    className="input"
                    aria-label={t('plantilles.tipus')}
                    value={p.tipus}
                    disabled={!editable}
                    autoFocus
                    onChange={(e) => setP({ ...p, tipus: e.target.value.toUpperCase() })}
                  />
                ) : (
                  <Desplegable<string>
                    aria-label={t('plantilles.tipus')}
                    valor={p.tipus}
                    disabled={!editable}
                    opcions={opcionsTipus}
                    onCanvia={(v) => {
                      if (v === ALTRE) {
                        setTipusLliure(true);
                        setP({ ...p, tipus: '' });
                      } else setP({ ...p, tipus: v });
                    }}
                  />
                )}
              </div>
              <label className="switch-row avisos-interruptor">
                {t('comu.actiu')}
                <input
                  type="checkbox"
                  className="switch"
                  checked={p.actiu}
                  disabled={!editable}
                  onChange={(e) => setP({ ...p, actiu: e.target.checked })}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="panel">
          <div className="panel__head">
            <h3>{t('avisos.plantilles.textos')}</h3>
            <div className="spacer" />
            <div className="segmented" role="group" aria-label={t('plantilles.idioma')}>
              {IDIOMES.map((i) => {
                const ok = completa(p.traduccions[i]);
                return (
                  <button
                    key={i}
                    type="button"
                    aria-pressed={idioma === i}
                    title={ok ? undefined : t('avisos.plantilles.incomplet')}
                    onClick={() => setIdioma(i)}
                  >
                    <span
                      className={`idioma-estat ${ok ? 'idioma-estat--ok' : 'idioma-estat--falta'}`}
                      aria-hidden="true"
                    />
                    {tc(`idioma.${i}`)}
                    {ok ? null : (
                      <span className="sr-only">{` · ${t('avisos.plantilles.incomplet')}`}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="panel__body form" ref={textos}>
            {!completa(tr) ? (
              <div className="avis-banda">
                <Icona nom="exclamationmark-triangle" />
                {t('avisos.plantilles.ajudaIncomplet', { idioma: tc(`idioma.${idioma}`) })}
              </div>
            ) : null}
            <div>
              <span className="camp__label">{t('plantilles.variables')}</span>
              <div className="chips mt-8">
                {p.variables.map((v) => (
                  <button
                    key={v}
                    type="button"
                    className="variable"
                    title={t('avisos.plantilles.insereixA', {
                      camp: t(`avisos.plantilles.camps.${ultimCamp}`),
                    })}
                    disabled={!editable}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => insereix(v)}
                  >
                    {`{{${v}}}`}
                  </button>
                ))}
                <span className="t-caption dim">
                  {t('avisos.plantilles.ajudaVariables', {
                    camp: t(`avisos.plantilles.camps.${ultimCamp}`),
                  })}
                </span>
              </div>
            </div>
            <Camp etiqueta={t('plantilles.titolMissatge')}>
              <input className="input" {...textCamp(C_TITOL)} />
            </Camp>
            <Camp etiqueta={t('plantilles.cos')}>
              <textarea className="input" rows={4} {...textCamp(C_COS)} />
            </Camp>
            <div className="grid grid-2" style={{ gap: 12 }}>
              <Camp etiqueta={t('plantilles.cosPush')}>
                <textarea className="input" rows={2} {...textCamp(C_PUSH)} />
              </Camp>
              <Camp etiqueta={t('plantilles.cosVeu')}>
                <textarea className="input" rows={2} {...textCamp(C_VEU)} />
              </Camp>
            </div>
            <ErrorDesa error={desa.error} />
          </div>
        </div>

        <div className="panel">
          <CapPanell titol={t('plantilles.previsualitzacio')}>
            {p.id != null ? (
              <button
                type="button"
                className="btn btn--ghost btn--xs"
                disabled={previa.isPending}
                onClick={() =>
                  previa.mutate({ id: p.id as number, canal, idioma, variables: valors })
                }
              >
                <Icona nom="eye" />
                {t('plantilles.previaServidor')}
              </button>
            ) : null}
          </CapPanell>
          <div className="panel__body form">
            {p.variables.length ? (
              <div className="grid grid-3" style={{ gap: 10 }}>
                {p.variables.map((v) => (
                  <Camp key={v} etiqueta={t('avisos.plantilles.valorDe', { variable: v })}>
                    <input
                      className="input"
                      value={valors[v] ?? ''}
                      onChange={(e) => setValors({ ...valors, [v]: e.target.value })}
                    />
                  </Camp>
                ))}
              </div>
            ) : null}
            <div className="segmented" role="group" aria-label={t('plantilles.canal')}>
              {CANALS_PREVIA.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-pressed={canal === c}
                  onClick={() => setCanal(c)}
                >
                  {tc(`canal.${c}`)}
                </button>
              ))}
            </div>
            <div className="previa-plantilla">
              {canal === 'PUSH' ? (
                <div className="previa-push previa-push--normal" data-canal="PUSH">
                  <div className="previa-push__ico">{tc('app.logo')}</div>
                  <div style={{ minWidth: 0 }}>
                    <div className="previa-push__app">{tc('app.descripcio')}</div>
                    <div className="previa-push__titol">{vista.titol}</div>
                    <div className="previa-push__cos">{vista.cos}</div>
                  </div>
                </div>
              ) : canal === 'EMAIL' ? (
                <div className="previa-correu" data-canal="EMAIL">
                  <div className="previa-correu__titol">{vista.titol}</div>
                  <div className="previa-correu__cos">{vista.cos}</div>
                </div>
              ) : (
                <div className="previa-correu" data-canal="TRUCADA">
                  <div className="hstack gap-6 previa-correu__titol">
                    <Icona nom="phone" className="c-green" />
                    {vista.titol}
                  </div>
                  <div className="previa-correu__cos">{vista.cos}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {editable ? (
        <BarraCanvis
          n={canvis}
          nom={titolDe(p) || t('plantilles.nova')}
          desant={desa.isPending}
          desactivat={!p.codi.trim() || !p.tipus.trim()}
          textDesa={p.id == null ? t('avisos.plantilles.crea') : undefined}
          onDesa={desar}
          onDescarta={() => {
            setP(base);
            setTipusLliure(false);
          }}
        >
          {!p.codi.trim() ? t('avisos.plantilles.senseCodi') : null}
        </BarraCanvis>
      ) : null}
    </div>
  );
}

export function W27Plantilles() {
  const { t } = useTranslation('administracio');
  const plantilles = usePlantilles();
  const guarda = useGuardaCanvis();
  const [sel, setSel] = useState<PlantillaDto | null>(null);
  const [cerca, setCerca] = useState('');
  const [tipus, setTipus] = useState('');
  const [inactives, setInactives] = useState(false);
  const llista = plantilles.data ?? [];
  const tipusExistents = [...new Set(llista.map((p) => p.tipus).filter(Boolean))].sort();
  const q = cerca.trim().toLowerCase();
  const visibles = llista.filter(
    (p) =>
      (inactives || p.actiu || p.id === sel?.id) &&
      (!tipus || p.tipus === tipus) &&
      (!q ||
        `${p.codi} ${p.tipus} ${p.traduccions.ca?.titol ?? ''} ${p.traduccions.es?.titol ?? ''}`
          .toLowerCase()
          .includes(q)),
  );
  const actual =
    (sel?.id != null ? llista.find((p) => p.id === sel.id) : null) ??
    sel ??
    visibles[0] ??
    llista[0] ??
    null;
  const tria = (p: PlantillaDto) => {
    if (p === sel || (p.id != null && p.id === actual?.id)) return;
    guarda(() => setSel(p));
  };

  return (
    <section className="pantalla" data-screen="W-27">
      <Toolbar titol={t('titols.plantilles')}>
        <Permis recurs="cai.plantilles" tipus="ALTA">
          <button
            type="button"
            className="btn btn--dark btn--s"
            onClick={() => guarda(() => setSel(novaPlantilla()))}
          >
            <Icona nom="plus" />
            {t('plantilles.nova')}
          </button>
        </Permis>
      </Toolbar>
      {plantilles.isError ? (
        <ErrorCarrega onReintenta={() => void plantilles.refetch()} />
      ) : (
        <div className="lf">
          <div className="lf__llista">
            <div className="lf__eines">
              <Cerca valor={cerca} onCanvia={setCerca} placeholder={t('avisos.plantilles.cerca')} />
              <div className="row" style={{ gap: 8 }}>
                <Desplegable<string>
                  variant="chip"
                  aria-label={t('plantilles.tipus')}
                  actiu={tipus !== ''}
                  valor={tipus}
                  opcions={[
                    { valor: '', etiqueta: t('avisos.plantilles.totsTipus') },
                    ...tipusExistents.map((x) => ({ valor: x, etiqueta: x })),
                  ]}
                  onCanvia={setTipus}
                />
              </div>
              <label className="switch-row t-footnote">
                {t('marc.mostraInactius')}
                <input
                  type="checkbox"
                  className="switch"
                  checked={inactives}
                  onChange={(e) => setInactives(e.target.checked)}
                />
              </label>
            </div>
            {llista.length === 0 && sel == null ? (
              <Buit icona="template">
                {plantilles.isLoading ? t('comu.carregant') : t('plantilles.buit')}
              </Buit>
            ) : (
              <ul className="lf__items" role="listbox" aria-label={t('plantilles.llista')}>
                {sel && sel.id == null ? (
                  <li className="lf__item" role="option" aria-selected>
                    <Icona nom="plus" />
                    <span className="lf__text">
                      <span className="lf__nom">{t('plantilles.nova')}</span>
                    </span>
                  </li>
                ) : null}
                {visibles.map((p) => (
                  <li
                    key={p.id ?? p.codi}
                    role="option"
                    tabIndex={0}
                    className={`lf__item${p.actiu ? '' : ' is-inactiu'}`}
                    aria-selected={actual?.id != null && actual.id === p.id}
                    onClick={() => tria(p)}
                    onKeyDown={(e) => e.key === 'Enter' && tria(p)}
                  >
                    <span className="lf__text">
                      <span className="lf__nom">{titolDe(p)}</span>
                      <span className="lf__sub mono">
                        {p.codi} · {p.tipus}
                      </span>
                    </span>
                  </li>
                ))}
                {visibles.length === 0 ? (
                  <li className="lf__buit">{t('avisos.senseResultats')}</li>
                ) : null}
              </ul>
            )}
          </div>
          {actual ? (
            <Editor
              key={actual.id ?? 'nova'}
              inicial={actual}
              tipusExistents={tipusExistents}
              onCreada={setSel}
            />
          ) : (
            <div className="lf__fitxa">
              <Buit icona="template">{t('plantilles.buit')}</Buit>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
